import React, { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { FormikErrors, useFormik } from 'formik'
import { useIntl } from 'react-intl'
import { toast } from 'sonner'
import { useForgotPassword } from '../../../../hooks/queries'

const initialValues = {
  email: '',
}


export function ForgotPassword() {
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const intl = useIntl()

  // Use React Query forgot password mutation
  const forgotPasswordMutation = useForgotPassword()
  /**
   * Defines the validation schema for the forgot password form and initializes the Formik form.
   * 
   * @remarks
   * This function sets up the Yup validation schema for the email field and configures the Formik form
   * with initial values, validation schema, and submit handler.
   * 
   * @param intl - The react-intl object for internationalization
   * @returns An object containing the Yup validation schema and the Formik form instance
   */
  const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage({ id: 'Wrong email format' }))
      .min(3, intl.formatMessage({ id: 'Minimum 3 symbols' }))
      .max(50, intl.formatMessage({ id: 'Maximum 50 symbols' }))
      .required(intl.formatMessage({ id: 'Email is required' })),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, { setStatus, setSubmitting, setErrors }) => {
      fetchForgotPassword(values, setStatus, setSubmitting, setErrors)
    },
  })


  /**
   * Handles the forgot password functionality using React Query
   *
   * @param values - An object containing the email address for password reset.
   * @param values.email - The email address of the user requesting a password reset.
   * @param setStatus - A function to set the status message of the form.
   * @param setSubmitting - A function to set the submitting state of the form.
   * @param setErrors - A function to set any errors that occur during the process.
   * @returns {Promise<void>} - A promise that resolves when the password reset request is complete.
   */
  const fetchForgotPassword = async (
    values: { email: string },
    setStatus: (status?: any) => void,
    setSubmitting: (isSubmitting: boolean) => void,
    setErrors: (
      errors: FormikErrors<{
        email: string
      }>
    ) => void
  ) => {
    setStatus('')
    try {
      await forgotPasswordMutation.mutateAsync({
        email: values.email,
      })

      setStatus('please confirm your email')
      toast.success('Please confirm your email')
      setHasErrors(false)
    } catch (error: any) {
      const errorData = error?.response?.data
      if (errorData) {
        setErrors(errorData)
      }
      setStatus(error?.message || 'An error occurred. Please try again.')
      setHasErrors(true)
      setSubmitting(false)
    }
  }

  return (
    <>
      <div className='card'>
        <div className='px-md-5 py-5 card-body'>
          <div className='d-flex flex-column justify-content-center align-items-center mb-10 p-8 rounded'>
            <h1>{intl.formatMessage({ id: 'Forgot Password' })}</h1>
            {formik.status ? (
              <div
                className={`font-weight-bold alert-text ${hasErrors ? 'alert-danger' : 'alert-success'
                  }`}
              >
                {intl.formatMessage({ id: formik.status })}

              </div>
            ) : (
              <span className='opacity-75'>
                {intl.formatMessage({ id: 'Enter your email address to reset your password.' })}
              </span>
            )}
          </div>
          <form noValidate id='kt_login_password_reset_form' onSubmit={formik.handleSubmit}>
            <div data-mdb-input-init className='mb-4 form-outline'>
              <input
                dir="ltr"
                type='email'
                id='form3Example3'
                className={clsx(
                  'form-control',
                  { 'is-invalid': formik.touched.email && formik.errors.email },
                  {
                    'is-valid': formik.touched.email && !formik.errors.email,
                  }
                )}
                {...formik.getFieldProps('email')}
                onChange={(e) => { formik.setFieldValue("email", e.target.value.toLowerCase()) }}
              />

              <div className='d-flex justify-content-between align-items-center py-1'>
                <label className='m-0 form-label' htmlFor='form3Example3'>
                  {intl.formatMessage({ id: 'Email address' })}

                </label>

                {formik.touched.email && formik.errors.email && (
                  <div className='text-center text-danger fv-plugins-message-container'>
                    <span role='alert'>{formik.errors.email}</span>
                  </div>
                )}
              </div>
            </div>

            <button
              type='submit'
              id='kt_sign_in_submit'
              className='btn-block w-100 btn btn-lg btn-primary'
              disabled={forgotPasswordMutation.isPending || !formik.isValid}
              style={{ opacity: 0.9 }}
            >
              {!forgotPasswordMutation.isPending && <span className='indicator-label'>
                {intl.formatMessage({ id: 'Submit' })}
              </span>}
              {forgotPasswordMutation.isPending && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  {intl.formatMessage({ id: 'Please wait' })}...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </form>
          <hr className='w-100' />
          <div className='form-group d-flex flex-wrap justify-content-between align-items-center mt-5 px-8'>
            <Link to='/auth' id='kt_login_forgot' className='font-weight-bold'>
              {intl.formatMessage({ id: 'Cancel' })}

            </Link>
          </div>
        </div>
      </div>
    </>
  )
}
